import { getTimeDifferenceInMinutes } from "~/modules/helpers";
import { FareDetail, Leg, Offer, OffersSummary, PassengerPricing } from "~/types";

export const filterByStops = (offers: Offer[], stops: string | null) => {
  if (!stops) return offers;
  const stopsNumber = parseInt(stops) + 1;
  return offers.filter(offer => {
    const outboundLegs = offer.itineraries.outbound.legs.length;
    const inboundLegs = offer.itineraries.inbound ? offer.itineraries.inbound.legs.length : 1;
    return outboundLegs <= stopsNumber && inboundLegs <= stopsNumber;
  });
};


export const filterByAirlines = (offers: Offer[], airlines: string[] | undefined) => {
  if (!airlines || airlines.length === 0) return offers;
  return offers.filter(offer => {
    const outboundLegs = offer.itineraries.outbound.legs;
    const inboundLegs = offer.itineraries.inbound ? offer.itineraries.outbound.legs : [];

    const outBoundValidAirline = outboundLegs.some((leg: Leg) => airlines.includes(leg.carrier.friendlyName));
    const inBoundValidAirline = inboundLegs.length ? outboundLegs.some((leg: Leg) => airlines.includes(leg.carrier.friendlyName)) : true;
    return outBoundValidAirline && inBoundValidAirline;
  });
};

export const filterByBaggage = (offers: Offer[], baggage: string | null) => {
  if (!baggage) return offers;
  const baggageValue = parseInt(baggage);
  return offers.filter(offer => {
    return offer.passengerPricing.every((passenger: PassengerPricing) =>
      passenger.fareDetailsByTripLeg.every((leg: FareDetail) => {
        const checkedBagsValue = parseInt(leg?.includedCheckedBags?.value || '0');
        if (baggageValue === 1) {
          return checkedBagsValue > 0;
        }
        return checkedBagsValue === 0;
      })
    );
  });
};

export const filterByDepartureTimeRange = (offers: Offer[], range: [number, number] | null) => {
  if (!range) return offers;
  const [start, end] = range;
  return offers.filter(offer => {
    const outbound = offer.itineraries.outbound.legs[0];
    const inbound = offer.itineraries.inbound ? offer.itineraries.inbound.legs[0] : null;

    const outboundDepartureTime = new Date(outbound.departure.dateTime).getHours();
    const outboundTimeValid = outboundDepartureTime >= start && outboundDepartureTime <= end;

    const inboundTimeValid = true;
    if (inbound) {
      const inboundDepartureTime = new Date(inbound.departure.dateTime).getHours();
      return inboundDepartureTime >= start && inboundDepartureTime <= end;
    }

    return outboundTimeValid && inboundTimeValid;
  });
};


export const filterByStopoverDuration = (offers: Offer[], range: [number, number] | null) => {
  if (!range) return offers;

  let [minDuration, maxDuration] = range;
  minDuration = minDuration * 60;
  maxDuration = maxDuration * 60;

  return offers.filter(offer => {
    const outboundLegs = offer.itineraries.outbound.legs;
    const inboundLegs = offer.itineraries.inbound ? offer.itineraries.inbound.legs : [];


    const outboundValid = outboundLegs.every((leg: Leg, index) => {
      const nextLeg = outboundLegs[index + 1] || null;
      if (!nextLeg) return true;

      const stopOverTime = getTimeDifferenceInMinutes(leg.arrival.dateTime, nextLeg.departure.dateTime);
      return stopOverTime >= minDuration && stopOverTime <= maxDuration;
    });


    const inboundValid = inboundLegs.length ? inboundLegs.every((leg: Leg, index) => {
      const nextLeg = inboundLegs[index + 1] || null;
      if (!nextLeg) return true;

      const stopOverTime = getTimeDifferenceInMinutes(leg.arrival.dateTime, nextLeg.departure.dateTime);
      return stopOverTime >= minDuration && stopOverTime <= maxDuration;
    }) : true;

    return outboundValid && inboundValid;
  });
};



export const filterByPriceRange = (offers: Offer[], range: [number, number] | null) => {
  if (!range) return offers;
  const [minPrice, maxPrice] = range;
  return offers.filter(offer => {
    const totalPrice = parseFloat(offer.price.total);
    return totalPrice >= minPrice && totalPrice <= maxPrice;
  });
};


export const parseIsoDuration = (duration: string): number => {
  const match = duration.match(/PT(\d+H)?(\d+M)?/);
  const hours = match && match[1] ? parseInt(match[1]) : 0;
  const minutes = match && match[2] ? parseInt(match[2]) : 0;
  return hours * 60 + minutes;
};


export const getOffersSummary = (offers: Offer[]): OffersSummary => {
  if (offers.length === 0) {
    return {
      minPrice: 0,
      maxPrice: 0,
      carriers: [],
    };
  }

  let minPrice = Infinity;
  let maxPrice = -Infinity;
  const carrierNames: Set<string> = new Set();

  offers.forEach(offer => {
    // Parse and update min/max price
    const price = parseFloat(offer.price.total);
    if (price < minPrice) minPrice = price;
    if (price > maxPrice) maxPrice = price;

    // Collect all carrier.friendlyName values
    const outboundLegs = offer.itineraries.outbound.legs;
    outboundLegs.forEach((leg: any) => {
      if (leg.carrier && leg.carrier.friendlyName) {
        carrierNames.add(leg.carrier.friendlyName);
      }
    });
  });

  return {
    minPrice,
    maxPrice,
    carriers: Array.from(carrierNames), // Convert the set to an array
  };
};
